<template>
  <div class="modal fade" id="raiseDisputeModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">
            <i class="mdi mdi-alert-circle-outline me-2"></i>
            Raise Billing/Payment Dispute
          </h4>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body">
          <form @submit.prevent="submitDispute">
            <div class="row">
              <!-- Customer Information -->
              <div class="col-md-6">
                <h6 class="mb-3 text-muted">Customer Information</h6>

                <div class="mb-3">
                  <label class="form-label">Customer Name <span class="text-danger">*</span></label>
                  <input v-model="disputeForm.customerName" type="text" class="form-control"
                         placeholder="Enter customer name" required>
                </div>

                <div class="mb-3">
                  <label class="form-label">Phone Number <span class="text-danger">*</span></label>
                  <input v-model="disputeForm.customerPhone" type="tel" class="form-control"
                         placeholder="+254xxxxxxxxx" required>
                </div>

                <div class="mb-3">
                  <label class="form-label">Email Address</label>
                  <input v-model="disputeForm.customerEmail" type="email" class="form-control"
                         placeholder="customer@email.com">
                </div>

                <div class="mb-3">
                  <label class="form-label">ID/Passport Number</label>
                  <input v-model="disputeForm.customerId" type="text" class="form-control"
                         placeholder="Enter ID/Passport number">
                </div>
              </div>

              <!-- Dispute Details -->
              <div class="col-md-6">
                <h6 class="mb-3 text-muted">Dispute Information</h6>

                <div class="mb-3">
                  <label class="form-label">Reference Number <span class="text-danger">*</span></label>
                  <input v-model="disputeForm.referenceNo" type="text" class="form-control"
                         placeholder="Bill/Receipt Number" required>
                  <small class="text-muted">Enter the bill or receipt number you're disputing</small>
                </div>

                <div class="mb-3">
                  <label class="form-label">Dispute Type <span class="text-danger">*</span></label>
                  <select v-model="disputeForm.disputeType" class="form-select" required>
                    <option value="">Select dispute type</option>
                    <option value="BILLING_ERROR">Billing Error</option>
                    <option value="PAYMENT_NOT_REFLECTED">Payment Not Reflected</option>
                    <option value="OVERCHARGE">Overcharge</option>
                    <option value="DUPLICATE_CHARGE">Duplicate Charge</option>
                    <option value="REFUND_REQUEST">Refund Request</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>

                <div class="mb-3">
                  <label class="form-label">Priority Level <span class="text-danger">*</span></label>
                  <select v-model="disputeForm.priority" class="form-select" required>
                    <option value="">Select priority</option>
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                    <option value="URGENT">Urgent</option>
                  </select>
                </div>

                <div class="mb-3">
                  <label class="form-label">Disputed Amount (KES) <span class="text-danger">*</span></label>
                  <input v-model="disputeForm.disputeAmount" type="number" step="0.01" class="form-control"
                         placeholder="0.00" required>
                </div>
              </div>
            </div>

            <!-- Description -->
            <div class="row">
              <div class="col-12">
                <div class="mb-3">
                  <label class="form-label">Detailed Description <span class="text-danger">*</span></label>
                  <textarea v-model="disputeForm.description" class="form-control" rows="4"
                            placeholder="Please provide a detailed description of your dispute..." required></textarea>
                  <small class="text-muted">Be as specific as possible to help us resolve your dispute quickly</small>
                </div>
              </div>
            </div>

            <!-- Supporting Documents -->
            <div class="row">
              <div class="col-12">
                <div class="mb-3">
                  <label class="form-label">Supporting Documents</label>
                  <input @change="handleFileUpload" type="file" class="form-control"
                         multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx">
                  <small class="text-muted">Upload receipts, bank statements, or other supporting documents (optional)</small>

                  <!-- File preview -->
                  <div v-if="uploadedFiles.length > 0" class="mt-2">
                    <div class="border rounded p-2">
                      <h6 class="mb-2">Uploaded Files:</h6>
                      <div v-for="(file, index) in uploadedFiles" :key="index" class="d-flex justify-content-between align-items-center mb-1">
                        <span class="text-truncate">{{ file.name }}</span>
                        <button @click="removeFile(index)" type="button" class="btn btn-outline-danger btn-sm">
                          <i class="mdi mdi-close"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Preferred Contact Method -->
            <div class="row">
              <div class="col-12">
                <div class="mb-3">
                  <label class="form-label">Preferred Contact Method</label>
                  <div class="form-check-group">
                    <div class="form-check form-check-inline">
                      <input v-model="disputeForm.preferredContact" class="form-check-input" type="radio"
                             value="PHONE" id="contactPhone">
                      <label class="form-check-label" for="contactPhone">Phone</label>
                    </div>
                    <div class="form-check form-check-inline">
                      <input v-model="disputeForm.preferredContact" class="form-check-input" type="radio"
                             value="EMAIL" id="contactEmail">
                      <label class="form-check-label" for="contactEmail">Email</label>
                    </div>
                    <div class="form-check form-check-inline">
                      <input v-model="disputeForm.preferredContact" class="form-check-input" type="radio"
                             value="SMS" id="contactSMS">
                      <label class="form-check-label" for="contactSMS">SMS</label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Terms and Conditions -->
            <div class="row">
              <div class="col-12">
                <div class="mb-3">
                  <div class="form-check">
                    <input v-model="disputeForm.agreeToTerms" class="form-check-input" type="checkbox"
                           id="agreeTerms" required>
                    <label class="form-check-label" for="agreeTerms">
                      I agree to the <a href="javascript: void(0);" class="text-primary">terms and conditions</a> and confirm that the information provided is accurate <span class="text-danger">*</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Submit Button -->
            <div class="d-flex justify-content-end gap-2">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button type="submit" :disabled="submitting" class="btn btn-primary">
                <span v-if="!submitting">Submit Dispute</span>
                <span v-else>
                  <div class="spinner-border spinner-border-sm me-2" role="status"></div>
                  Submitting...
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  name: "RaiseDisputeModal",
  data() {
    return {
      submitting: false,
      uploadedFiles: [],
      disputeForm: {
        customerName: '',
        customerPhone: '',
        customerEmail: '',
        customerId: '',
        referenceNo: '',
        disputeType: '',
        priority: 'MEDIUM',
        disputeAmount: '',
        description: '',
        preferredContact: 'PHONE',
        agreeToTerms: false
      }
    }
  },
  methods: {
    handleFileUpload(event) {
      const files = Array.from(event.target.files);
      files.forEach(file => {
        if (file.size <= 5 * 1024 * 1024) { // 5MB limit
          this.uploadedFiles.push(file);
        } else {
          alert(`File ${file.name} is too large. Maximum size is 5MB.`);
        }
      });
      event.target.value = ''; // Reset input
    },

    removeFile(index) {
      this.uploadedFiles.splice(index, 1);
    },

    async submitDispute() {
      try {
        this.submitting = true;

        // Validate form
        if (!this.validateForm()) {
          this.submitting = false;
          return;
        }

        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Emit event to parent component
        this.$emit('dispute-raised', {
          ...this.disputeForm,
          supportingDocuments: this.uploadedFiles
        });

        // Reset form
        this.resetForm();

        // Close modal
        const modalElement = document.getElementById('raiseDisputeModal');
        if (modalElement) {
          const bootstrapModal = bootstrap.Modal.getInstance(modalElement);
          if (bootstrapModal) {
            bootstrapModal.hide();
          }
        }

        this.submitting = false;
      } catch (error) {
        this.submitting = false;
        alert('Error submitting dispute: ' + error.message);
      }
    },

    validateForm() {
      // Basic validation
      if (!this.disputeForm.customerName.trim()) {
        alert('Customer name is required');
        return false;
      }
      if (!this.disputeForm.customerPhone.trim()) {
        alert('Phone number is required');
        return false;
      }
      if (!this.disputeForm.referenceNo.trim()) {
        alert('Reference number is required');
        return false;
      }
      if (!this.disputeForm.disputeType) {
        alert('Dispute type is required');
        return false;
      }
      if (!this.disputeForm.disputeAmount || this.disputeForm.disputeAmount <= 0) {
        alert('Valid dispute amount is required');
        return false;
      }
      if (!this.disputeForm.description.trim()) {
        alert('Description is required');
        return false;
      }
      if (!this.disputeForm.agreeToTerms) {
        alert('You must agree to the terms and conditions');
        return false;
      }
      return true;
    },

    resetForm() {
      this.disputeForm = {
        customerName: '',
        customerPhone: '',
        customerEmail: '',
        customerId: '',
        referenceNo: '',
        disputeType: '',
        priority: 'MEDIUM',
        disputeAmount: '',
        description: '',
        preferredContact: 'PHONE',
        agreeToTerms: false
      };
      this.uploadedFiles = [];
    }
  },
  watch: {
    // Reset form when modal is opened
    '$route'() {
      this.resetForm();
    }
  }
}
</script>

<style scoped>
.form-check-group {
  display: flex;
  gap: 1rem;
}

.text-truncate {
  max-width: 200px;
}

.spinner-border-sm {
  width: 0.875rem;
  height: 0.875rem;
}

.form-label {
  font-weight: 600;
}

textarea.form-control {
  resize: vertical;
  min-height: 100px;
}
</style>